import { useRef } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    ModeConfig,
    TSTFilterBar,
    TSTPagination,
    TSTTable,
} from "@impero-com/ui";
import { FilterTarget } from "@impero-com/ui/domain";
import { Column, RowData, Table } from "@tanstack/react-table";

interface AssignmentTableProps<ROW extends RowData> {
    table: Table<ROW>;
    getFilterComponent: (
        column: Column<ROW>,
        target: FilterTarget
    ) => React.ReactNode;
    tableId: string;
    modeConfig?: ModeConfig;
    getModeConfig?: (column: Column<ROW>) => ModeConfig | undefined;
    canAddException?: (column: Column<ROW>) => boolean;
    toolbar?: React.ReactNode;
}

/**
 * This components is shared by most user pages, if you plan on adding a lot
 * of custom behavior, consider creating a new component instead of adding
 * props here.
 */
export function AssignmentTable<ROW extends RowData>({
    table,
    tableId,
    getFilterComponent,
    modeConfig,
    getModeConfig,
    canAddException,
    toolbar,
}: AssignmentTableProps<ROW>) {
    const { t } = useTranslation();
    const tableRef = useRef<HTMLDivElement>(null);

    return (
        <div>
            <TSTFilterBar<ROW>
                table={table}
                tableId={tableId}
                addLabel={t("FilterBar_AddFilter")}
                globalSearchLabel={t("FilterBar_GlobalSearchPlaceholder")}
                getFilterCountLabel={(count) =>
                    t("FilterBar_FiltersApplied", { count })
                }
                rowCountLabel={t("FilterBar_RowCount", {
                    count: table.getPrePaginationRowModel().rows.length,
                })}
                hideFiltersLabel={t("FilterBar_HideFilters")}
                clearAllLabel={t("FilterBar_ClearAll")}
                getFilterComponent={getFilterComponent}
                modeConfig={modeConfig}
                getModeConfig={getModeConfig}
                canAddException={canAddException}
                addExceptionLabel={t("FilterBar_AddException")}
            />
            <TSTTable
                tableRef={tableRef}
                table={table}
                noResultsFallback={t("NoResults")}
                noSourceRowsFallback={t("NoResults")}
                toolbar={toolbar}
            />
            <TSTPagination
                tableElement={tableRef}
                table={table}
                pageLabel={t("Pagination_Page")}
                getRowCountLabel={(shown, count) =>
                    t("Pagination_RowCount", { shown, count })
                }
                getSinglePageRowCountLabel={(count) =>
                    t("Pagination_RowCountSinglePage", { count })
                }
            />
        </div>
    );
}
